import { useEffect, useState } from 'react';
import {
  Alert, Box, Button, Chip, CircularProgress, Divider, Input, Sheet, Stack, Typography,
} from '@mui/joy';
import ContentCopyRoundedIcon from '@mui/icons-material/ContentCopyRounded';
import GroupRoundedIcon from '@mui/icons-material/GroupRounded';

const STATUS_LABEL = { pending: 'Ожидает', confirmed: 'Подтверждён', rejected: 'Отклонён' };
const STATUS_COLOR = { pending: 'neutral', confirmed: 'success', rejected: 'danger' };

/** Блок «Реферальная программа». props: fetcher (async ()=>payload), title? */
export default function ReferralBlock({ fetcher, title = 'Реферальная программа' }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    let alive = true;
    setLoading(true); setError(null);
    (async () => {
      try { const r = await fetcher(); if (alive) setData(r); }
      catch { if (alive) setError('Не удалось загрузить'); }
      finally { if (alive) setLoading(false); }
    })();
    return () => { alive = false; };
  }, [fetcher]);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(data?.link || data?.code || '');
      setCopied(true); setTimeout(() => setCopied(false), 1500);
    } catch { /* ignore */ }
  };

  return (
    <Sheet variant="outlined" sx={{ p: 2, borderRadius: 'lg', mb: 2 }}>
      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
        <GroupRoundedIcon color="primary" />
        <Typography level="title-md">{title}</Typography>
      </Stack>
      <Typography level="body-sm" color="neutral" sx={{ mb: 1.5 }}>
        Приглашайте в сервис по своей ссылке — мы зафиксируем, кто пришёл от вас.
      </Typography>

      {loading ? (
        <Stack alignItems="center" sx={{ py: 3 }}><CircularProgress size="sm" /></Stack>
      ) : error ? (
        <Alert color="danger">{error}</Alert>
      ) : (
        <>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ mb: 2 }}>
            <Input readOnly value={data?.link || ''} sx={{ flex: 1 }} />
            <Button startDecorator={<ContentCopyRoundedIcon />} onClick={copy}
              color={copied ? 'success' : 'primary'}>
              {copied ? 'Скопировано' : 'Копировать'}
            </Button>
          </Stack>

          <Divider sx={{ mb: 1.5 }} />
          <Typography level="title-sm" sx={{ mb: 1 }}>Мои рефералы: {data?.total || 0}</Typography>

          {(data?.referrals || []).length === 0 ? (
            <Typography level="body-sm" color="neutral">Пока никого — поделитесь ссылкой.</Typography>
          ) : (
            <Stack spacing={1}>
              {data.referrals.map((r) => (
                <Stack key={r.id} direction="row" justifyContent="space-between" alignItems="center">
                  <Box>
                    <Typography level="body-sm" sx={{ fontWeight: 600 }}>{r.referred_name}</Typography>
                    <Typography level="body-xs" color="neutral">
                      {r.referred_kind_label} · {new Date(r.date_create).toLocaleDateString('ru-RU')}
                    </Typography>
                  </Box>
                  <Chip size="sm" variant="soft" color={STATUS_COLOR[r.status] || 'neutral'}>
                    {STATUS_LABEL[r.status] || r.status}
                  </Chip>
                </Stack>
              ))}
            </Stack>
          )}
        </>
      )}
    </Sheet>
  );
}